import { Component, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { Transaction, ORDER_STATUS, TRANSACTION_CATEGORY_TYPE, ORDER_STATUS_DISPLAYED } from '../../model/myria/app/transaction';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { getCurrencySymbol } from '@angular/common';
import { AlertController } from 'ionic-angular';
import { LoadingProvider } from '../../providers/loading/loading';
import { OrderProvider } from '../../providers/order/order';
import { SendAgainProvider } from '../../providers/send-again/send-again';
import { CustomerProvider } from '../../providers/customer/customer';
import { AnalyticsEventType, ContactModalReasons, SegmentLocationNames } from '../../model/types/analytics.const';
import { RouteProvider } from '../../providers/route/route';
import { PaymentMethodType } from '../../model/types/payment-method-type';
import { SegmentProvider } from '../../providers/analytics/segment';
import { APP_CONFIG, Region } from '../../environment';
import { DELIVERY_METHOD } from '../../providers/myria/app-variables';
import { UtilsProvider } from '../../providers/myria/utils-provider';
import { LoggerProvider, LoggerEvent } from '../../providers/logger/logger';
import { CallProvider } from '../../providers/call/call';
import { ColorProvider } from '../../providers/color/color';
import { RmtTranslateComponent } from '../../components/rmt-translate/rmt-translate';
import { HomeScreenProvider } from '../../providers/homescreen/homescreen';
import { DocType } from '../../model/types/doc-type';
import { LoginProvider } from '../../providers/myria/login-provider';

@Component({
  selector: 'activity-item-new',
  templateUrl: 'activity-item-new.html'
})
export class ActivityItemNewComponent {
  @ViewChild('share') share;
  @Input() transaction: Transaction;
  @Input() cardId: string;
  @Input() primaryBtndId: string;
  @Input() secondaryBtnId: string;
  @Input() transactionBeneficiaryId: string;
  @Input() showDateHeader = true;
  @Input() lineNumber = 0;
  @Input() isFromNewHomeScreen = false;

  @Output() selectEmitter: EventEmitter<any> = new EventEmitter();

  primaryButtonText: string;
  primaryAction: any;
  primaryButtonClass: string = '';

  sendLabel: string;
  beneficiaryName: string;
  statusIcon: string;
  statusTextColor: string;
  statusText: string;
  dateShown;
  greyedOut: boolean; // true if transaction is older than 1 week
  subscription;

  ngOnInit() {
    this.init();
    this.buildName();
    this.subscription =  this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.dateShown = this.utilsProvider.getFormattedActivityTransactionDate(this.transaction.Date.toString());
    });
  }
  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  constructor(
    private alertCtrl: AlertController,
    private translate: TranslateService,
    private loadingProvider: LoadingProvider,
    private orderProvider: OrderProvider,
    private routeProvider: RouteProvider,
    private analytics: SegmentProvider,
    private call: CallProvider,
    private utilsProvider: UtilsProvider,
    private sendAgainProvider: SendAgainProvider,
    private homescreenProvider: HomeScreenProvider,
    private customerProvider: CustomerProvider,
    private loginProvider: LoginProvider
  ) { }

  init() {
    this.dateShown = this.utilsProvider.getFormattedActivityTransactionDate(this.transaction.Date.toString());
    this.initActivityActionBtn();
    this.initShownOrderStatusActionBtn();

    if (!this.transaction.ShownOrderStatus)
      this.transaction.ShownOrderStatus = this.translate.instant(this.statusText);
  }

  initShownOrderStatusActionBtn() {
    switch (this.transaction.OrderStatusDisplayed) {
      case ORDER_STATUS_DISPLAYED.CONFIRMATION_EXPIRED:
          this.statusIcon = "assets/icons/activity_ic_expired_or_cancelled.svg";
        this.statusText =
          "page.activityPage.orderStatus.ria_myaccount_orderstatus_confirmationexpired";
        this.initSendAgainButtons();
        break;
      case ORDER_STATUS_DISPLAYED.DOCUMENT_REQUIRED:
          this.statusIcon = 'assets/icons/activity_ic_paymentpending.svg';
          this.statusText = 'page.activityPage.orderStatus.ria_myaccount_orderstatus_documentsrequired';
          this.primaryButtonClass = 'btn-primary-background';
          this.initUploadButtons();
          break;
      case ORDER_STATUS_DISPLAYED.DOCUMENT_REVIEW:
          this.statusIcon = 'assets/icons/activity_ic_document_review.svg';
          this.statusText = 'page.activityPage.orderStatus.ria_myaccount_orderstatus_documentsinreview';
          this.primaryButtonText = null;
          break;
      case ORDER_STATUS_DISPLAYED.DOCUMENT_RESUBMIT:
        this.statusIcon = 'assets/icons/activity_ic_paymentpending.svg';
        this.primaryButtonClass = 'btn-primary-background';
        this.statusText = 'page.transferDetailsPage.uploadDocs.resubmitDocumentsLabel';
        this.initUploadButtons();
        break;
    }
  }

  initActivityActionBtn() {
    switch (this.transaction.StatusId) {
      case ORDER_STATUS.PAYID_DEREGISTERED:
      case ORDER_STATUS.PAYMENT_PENDING:
        this.statusIcon = 'assets/icons/activity_ic_paymentpending.svg';
        this.primaryButtonClass = 'btn-primary-background';
        this.statusText = (this.transaction.StatusId == ORDER_STATUS.PAYMENT_PENDING) ? 'global.myRia.jsonContent.OrderStatusPaymentPending' : 'page.activityPage.payIDExpireNoticeText';
        if(this.transaction.StatusId == ORDER_STATUS.PAYMENT_PENDING)
          this.initFundButtons();
        break;
      case ORDER_STATUS.UNFUNDED:
      case ORDER_STATUS.PAYMENT_PENDING:
        this.statusIcon = 'assets/icons/activity_ic_paymentpending.svg';
        this.primaryButtonClass = 'btn-primary-background';
        this.statusText = 'global.myRia.jsonContent.OrderStatusPaymentPending';
        this.initFundButtons();
        break;
      case ORDER_STATUS.COMPLETED:
        this.statusIcon = 'assets/icons/activity_ic_received.svg';
        this.statusText = 'page.activityPage.orderStatus.ria_myaccount_orderstatus_received';
        this.initSendAgainButtons();
        break;
      case ORDER_STATUS.PROCESSING:
        this.statusIcon = 'assets/icons/activity-ic-inprogress.svg';
        this.statusText = 'page.activityPage.orderStatus.ria_myaccount_orderstatus_inprogress';
        this.initShareButtons();
        break;
      case ORDER_STATUS.PENDING:
        this.statusIcon = 'assets/icons/activity_ic_directbank_pending.svg';
        this.statusText = 'page.activityPage.orderStatus.ria_myaccount_orderstatus_pending';
        break;
      case ORDER_STATUS.BENEFICIARY_CAN_COLLECT:
        this.statusIcon = 'assets/icons/activity_ic_ready_for_pickup.svg';
        this.statusText = 'page.activityPage.orderStatus.ria_myaccount_orderstatus_readyforpickup';
        this.initShareButtons();
        break;
      case ORDER_STATUS.COLLECTED_BY_BENEFICIARY:
        this.statusIcon = 'assets/icons/activity_ic_received.svg';
        this.statusText = 'page.activityPage.orderStatus.ria_myaccount_orderstatus_received';
        this.initSendAgainButtons();
        break;
      case ORDER_STATUS.AVAILABLE_TO_REFUND:
        this.statusIcon = 'assets/icons/activity_ic_refund_pending.svg';
        this.statusText = 'page.activityPage.orderStatusRefundPending';
        this.initSendAgainButtons();
        break;
      case ORDER_STATUS.CANCELLED:
        this.statusIcon = 'assets/icons/activity_ic_expired_or_cancelled.svg';
        this.statusText = this.transaction.OrderStatusCategory == TRANSACTION_CATEGORY_TYPE.REQUEST_CANCELLED ?
                          'page.activityPage.orderStatus.ria_myaccount_orderstatus_tocancel' : 'page.activityPage.orderStatus.ria_myaccount_orderstatus_canceled';
        this.initSendAgainButtons();
        break;
      case ORDER_STATUS.EXPIRED:
        this.statusIcon = 'assets/icons/activity_ic_expired_or_cancelled.svg';
        this.statusText = 'page.activityPage.orderStatus.ria_myaccount_orderstatus_confirmationexpired';
        this.initSendAgainButtons();
        break;
      case ORDER_STATUS.REFUNDED:
        this.statusIcon = 'assets/icons/activity_ic_refunded.svg';
        this.statusText = 'page.activityPage.orderStatusRefunded';
        this.initSendAgainButtons();
        break;
   }
  }



  initFundButtons() {
    this.primaryButtonText = 'page.activityPage.fundTransfer';
    this.primaryAction = () => this.fundAction();
  }

  initShareButtons() {
    this.primaryButtonText = 'page.activityPage.shareButton';
    this.primaryAction = () => this.shareAction();
  }

  initUploadButtons() {
    this.primaryButtonText = 'page.activityPage.uploadButton';
    this.primaryAction = () => this.uploadAction(this.transaction);
  }

  async uploadAction(transaction: Transaction) {
    this.loadingProvider.show();

    try {
      const isFromMalaysiaAndNonNational = await this.customerProvider.isFromMalaysiaAndNonNational();
      this.orderProvider.logDocUploadEvent(AnalyticsEventType.DocUploadStarted, transaction, isFromMalaysiaAndNonNational);
      this.loadingProvider.hide();

      if (isFromMalaysiaAndNonNational && this.customerProvider.isDocUploadRequired(transaction.UploadDocumentInfo))
        this.customerProvider.navigateToDocUploadForMY(this.routeProvider, transaction, true);
      else
        this.routeProvider.openModal(this.routeProvider.pages.DocUploadSelectPage, { transaction: this.transaction });
    }
    catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.GeneralException, 'Failed to get customer details in activity page');
      this.loadingProvider.hide();
    }
  }

  getEventDate(dateFormat: string) {
    let eventDate = '';
    if (dateFormat) {
      try {
        const dateTransaction = new Date(dateFormat);

        const currentDate = new Date();
        const utc1 = Date.UTC(dateTransaction.getFullYear(), dateTransaction.getMonth(), dateTransaction.getDate());
        const utc2 = Date.UTC(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
        // find number of days passed since transaction
        if (Math.floor((utc2 - utc1) / (1000 * 60 * 60 * 24)) > 7)
          this.greyedOut = true;
        else
          this.greyedOut = false;

        eventDate = this.utilsProvider.getFormattedDate(dateTransaction);
      } catch (ex) {
        LoggerProvider.exception(this, ex, LoggerEvent.PageException, 'Failed to get transaction date content in activity page');
      }
    }
    return eventDate;
  }


  initSendAgainButtons() {
    if (this.transaction.CanResend === false) {
      this.primaryButtonText = null;
      this.primaryAction = null;
      return;
    }
    this.primaryButtonText = 'page.activityPage.sendAgainButton';
    this.primaryAction = () => this.sendAgainAction();
  }

  emit() {
    this.selectEmitter.emit();
  }

  shareAction() {
    if (!this.share.orderPromise)
      this.share.orderPromise = this.orderProvider.getOrderStatus(this.transaction.OrderNo);
    this.share.shareTransactionDetails();
  }

  buildName() {
    this.beneficiaryName = this.transaction.BeneficiaryFirstName + ' ' + this.transaction.BeneficiaryFirstLastName;
  }

  primaryButtonAction(ev) {
    ev.stopPropagation();
    ev.preventDefault();
    if (this.primaryAction) this.primaryAction();

  }

  getCurrencyIcon() {
    const icon = getCurrencySymbol(this.transaction.Currency, 'wide');
    return icon;
  }

  async fundAction() {
    this.loadingProvider.show();
    try {
      const order = await this.orderProvider.getOrderStatus(this.transaction.OrderNo);

      this.analytics.trackEvent({ eventName: AnalyticsEventType.FundingStarted, analyticsData: this.orderProvider.getAnalyticsDataFromTransaction(this.transaction, order) });

      if (APP_CONFIG.region.region === Region.CL) {
        if (order.paymentMethod === PaymentMethodType.SencillitoOnline) {
          this.routeProvider.openModal(this.routeProvider.pages.SendPaymentPendingSencillito, { transaction: this.transaction, paymentMethod: this.transaction.PaymentMethod });
        }
        else {
          this.routeProvider.openModal(this.routeProvider.pages.SendPaymentPendingPayInStore, { transaction: this.transaction, paymentMethod: this.transaction.PaymentMethod });
        }
      }
      else if(order.paymentMethod === PaymentMethodType.PayID) this.routeProvider.openModal(this.routeProvider.pages.SendPaymentPendingPayID, { transaction: this.transaction, paymentMethod: this.transaction.PaymentMethod});
      else if(order.paymentMethod === PaymentMethodType.StagedOnline) this.orderProvider.setPaynowRoute(this.transaction, true); // set true for show expried alert on paynow
      else if (order.paymentMethod === PaymentMethodType.StagedRia) {
        if (this.orderProvider.hasBarcodeToShow(this.transaction, order)) {
          this.orderProvider.setBarcodeRoute(this.transaction, order, false, true);
        }
        else {
          this.routeProvider.openActionSheetNew(
            this.routeProvider.pages.ModalAlertPage,
            {
              title: this.translate.instant('global.callCustomerServiceButton'),
              subtitle: this.translate.instant('global.CustomerServiceHours'),
              primaryButtonText: this.translate.instant('page.sendReceiptPage.consumerFraudAlertModal.callButton'),
              trackEvent: AnalyticsEventType.ContactModalDisplayed,
              contactCTAReason: ContactModalReasons.StagedBarcodeNotReturned,
              trackParams: {
                location: SegmentLocationNames.Activity,
                reason: ContactModalReasons.StagedBarcodeNotReturned,
              },
              primaryAction: () => this.call.callCustomerService(SegmentLocationNames.Activity, ContactModalReasons.StagedBarcodeNotReturned),
            },
          );
        }
      }
      else {
        this.homescreenProvider.paymentAgent = PaymentMethodType.PayNearMe;
        this.routeProvider.openModal(this.routeProvider.pages.SendPayNearMePage, { order: order });
      }
      this.loadingProvider.hide();
    }
    catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to get order details in activity page');
    }
  }

  async sendAgainAction() {
    if (this.loginProvider.isUserFraudBlocked())
      return this.utilsProvider.redirectToRestrictedTransfers();

    this.loadingProvider.show();
    this.analytics.trackEvent({ eventName: AnalyticsEventType.TransferResendStarted, analyticsData: {
      location: this.isFromNewHomeScreen ? SegmentLocationNames.HomeScreen : SegmentLocationNames.Activity,
      lineNumber: this.lineNumber,
    }});

    try {
      const sendAgainOrder = await this.orderProvider.getOrderStatus(this.transaction.OrderNo, true);
      let header = this.translate.instant('page.activityPage.sendAgainButton');
      this.transaction.TransferAmount = null;
      this.loadingProvider.showDelayed();
      await this.sendAgainProvider.processSendAgain(sendAgainOrder, header);
      this.loadingProvider.hide();
    }
    catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to get order details in activity page');
      this.sendAgainProvider.showFallbackErrorModal();
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/activity-item-new/activity-item-new.ts